'use client';
import { useEffect, useMemo } from 'react';
import { ProductJsonLd } from 'next-seo';
import { useResponsive } from '@/hooks/useResponsive';
import { useProductTracking } from '@/hooks/useProductTracking';
import { useStrapiPDP } from '@/hooks/useStrapiPDP';
import { ProductBase, ProductDetail } from '@/types/product';
import ContentWrapper from '@/components/common/ContentWrapper';
import MobileWrapper from '@/components/common/MobileWrapper';
import {
  BreadcrumbMobile,
  FAQSectionMobile,
  OtherProductsSectionMobile,
  ProductDetailsMobile,
  ProductGalleryMobile,
  ProductInfoSectionMobile,
  RelatedProductsSectionMobile,
} from '@/components/sections/mobile';
import Breadcrumb from '@/components/sections/Breadcrumb';
import ProductLayout from '@/components/sections/ProductLayout';
import ProductGallery from '@/components/sections/ProductGallery';
import ProductDetails from '@/components/sections/ProductDetails';
import ProductInfoSection from '@/components/sections/ProductInfoSection';
import { FAQSection, OtherProductsSection, RelatedProductsSection } from '@/components/sections';
import { useCartStore } from '@/store/useCartStore';
import { CartItem } from '@/types/cart';
import { useCartTracking } from '@/hooks/useCartTracking';
import { BreadcrumbMenu } from '@/types/menu';
import { BreadcrumbItem } from '@/types/common';
import { fetchProductStock } from '@/hooks/useProducts';
import { useToastStore } from '@/store/useToastStore';
import { useAddToCart } from '@/hooks/useAddToCart';
import { trackMetaPixelEvent } from '@/lib/metaPixel';

const SITE_ORIGIN = process.env.NEXT_PUBLIC_SITE_URL || 'https://hiperalia.com';

function toAbsoluteImageUrl(src: string): string | null {
  const trimmed = src?.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return `${SITE_ORIGIN}${path}`;
}

function productDescriptionPlain(product: ProductDetail): string {
  const fromShort = product.shortDescription
    ?.replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (fromShort) return fromShort;
  const fromMeta = product.metaDescription?.trim();
  if (fromMeta) return fromMeta;
  const fromDesc = product.description
    ?.replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (fromDesc) return fromDesc;
  return product.title;
}

interface ProductPageClientProps {
  product: ProductDetail;
  relatedProducts: ProductBase[];
  otherProducts: ProductBase[];
  breadcrumbs?: { label: string; href?: string; isCurrent?: boolean }[];
}

const ProductPageClient: React.FC<ProductPageClientProps> = ({
  product,
  relatedProducts,
  otherProducts,
  breadcrumbs: breadcrumbsProp,
}) => {
  const { addToCart } = useAddToCart();
  const { isMobileOrTablet, isLoaded } = useResponsive();
  /** Un solo layout en el DOM: evita H1/H2 duplicados en auditorías SEO */
  const showMobileLayout = isLoaded && isMobileOrTablet;
  const showDesktopLayout = !showMobileLayout;
  const { trackProductView } = useProductTracking();
  // Obtener datos de Strapi CMS
  const {
    pdpData,
    loading: strapiLoading,
    error: strapiError,
    getNutritionalInfo,
    getIngredients,
    getSugerenciasDegustacion,
    getRelatedProducts,
    getOtherCustomersTitle,
    getFAQs,
    isOtherCustomersVisible,
    isFAQsVisible,
    extractHTMLFromStrapiText,
  } = useStrapiPDP(product.id.toString());

  //const allReviews = [...userReviews, ...productReviews];

  // Trackear la visualización del producto cuando el componente se monta
  useEffect(() => {
    if (product) {
      trackProductView({
        item_id: product.id.toString(),
        item_name: product.title,
        item_category: product.breadcrumb?.[0]?.name || '',
        item_category2: product.breadcrumb?.[1]?.name || '',
        item_category3: product.breadcrumb?.[2]?.name || '',
        item_brand: product.brand,
        price: product.priceFinalInc,
        bar_code: product.barcode,
      });

      // Meta Pixel: ViewContent
      trackMetaPixelEvent('ViewContent', {
        content_ids: [product.id.toString()],
        content_name: product.title,
        content_type: 'product',
        value: product.priceFinalInc,
        currency: 'EUR',
      });
    }
  }, [product]);

  const handleAddToCart = async (quantity: number, format: string) => {
    // Crear el item del carrito usando el store global
    const cartItem: CartItem = {
      id: product.id.toString(),
      name: product.title,
      image: product.images?.[0]?.src || product.image || '',
      price: product.priceFinalInc,
      tax: product.priceFinalInc - product.priceFinalExc,
      category: product.breadcrumb?.[0]?.name || '',
      category2: product.breadcrumb?.[1]?.name || '',
      category3: product.breadcrumb?.[2]?.name || '',
      variant: format,
      sku: product.slug || product.reference || '',
      quantity,
      weight: product.weight || '',
      brand: product.brand || '',
      format: format,
      barcode: product.barcode || '',
    };
    addToCart(cartItem, quantity);
  };

  const handleReviewSubmit = (review: any) => {
    //addReview(review);
    alert('Opinión publicada correctamente');
  };

  const handleProductFavoriteToggle = (id: string) => {
    alert(`Producto ${id} añadido/quitado de favoritos`);
  };

  const breadcrumbMenu: BreadcrumbMenu = {
    menuLevel1: {
      id: product.breadcrumb?.[0]?.id || 0,
      description: '',
      level: 0,
      parent: 0,
      name: product.breadcrumb?.[0]?.name || '',
      slug: product.breadcrumb?.[0]?.slug || '',
      pageNumbers: 0,
      url: `/${product.breadcrumb?.[0]?.slug || ''}`,
    },
    menuLevel2: {
      id: product.breadcrumb?.[1]?.id || 0,
      description: '',
      level: 0,
      parent: 0,
      name: product.breadcrumb?.[1]?.name || '',
      slug: product.breadcrumb?.[1]?.slug || '',
      pageNumbers: 0,
      url: `/${product.breadcrumb?.[0]?.slug}/${product.breadcrumb?.[1]?.slug}`,
    },
    menuLevel3: {
      id: product.breadcrumb?.[2]?.id || 0,
      description: '',
      level: 0,
      parent: 0,
      name: product.breadcrumb?.[2]?.name || '',
      slug: product.breadcrumb?.[2]?.slug || '',
      pageNumbers: 0,
      url: `/${product.breadcrumb?.[0]?.slug}/${product.breadcrumb?.[1]?.slug}/${product.breadcrumb?.[2]?.slug}`,
    },
  };

  // Usar breadcrumbs de la prop si están disponibles, sino construir localmente
  const breadcrumbs: BreadcrumbItem[] = breadcrumbsProp
    ? breadcrumbsProp
    : (() => {
      const localBreadcrumbs: BreadcrumbItem[] = [];
      if (product.breadcrumb?.[0]?.name) {
        localBreadcrumbs.push({
          label: product.breadcrumb?.[0]?.name,
          href: `/${product.breadcrumb?.[0]?.slug}`,
        });
      }
      if (product.breadcrumb?.[1]?.name) {
        localBreadcrumbs.push({
          label: product.breadcrumb?.[1]?.name,
          href: `/${product.breadcrumb?.[0]?.slug}/${product.breadcrumb?.[1]?.slug}`,
        });
      }
      if (product.breadcrumb?.[2]?.name) {
        localBreadcrumbs.push({
          label: product.breadcrumb?.[2]?.name,
          href: `/${product.breadcrumb?.[0]?.slug}/${product.breadcrumb?.[1]?.slug}/${product.breadcrumb?.[2]?.slug}`,
        });
      }
      localBreadcrumbs.push({ label: product.title || '', isCurrent: true });
      return localBreadcrumbs;
    })();

  const productImageUrls = useMemo(
    () =>
      [
        ...new Set(
          [product.image, ...(product.images?.map((img) => img.src) ?? [])]
            .map((src) => toAbsoluteImageUrl(src || ''))
            .filter((u): u is string => Boolean(u))
        ),
      ],
    [product.image, product.images]
  );

  const barcodeExtra = useMemo(() => {
    const code = product.barcode?.trim();
    if (!code) return {};
    return { gtin: code };
  }, [product.barcode]);

  return (
    <ContentWrapper data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:234:4" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="234" data-component-file="ProductPageClient.tsx" data-component-name="ContentWrapper" data-component-content="%7B%22elementName%22%3A%22ContentWrapper%22%7D">
      <ProductJsonLd data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:235:6" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="235" data-component-file="ProductPageClient.tsx" data-component-name="ProductJsonLd" data-component-content="%7B%22elementName%22%3A%22ProductJsonLd%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
        name={product.title}
        description={productDescriptionPlain(product)}
        brand={product.brand || 'Hiperalia'}
        sku={String(product.id)}
        image={productImageUrls.length ? productImageUrls[0] : undefined}
        {...barcodeExtra}
        offers={{
          price: product.priceFinalInc.toFixed(2),
          priceCurrency: 'EUR',
          availability: 'https://schema.org/InStock',
          url: `${SITE_ORIGIN}/${product.slug}`,
          seller: {
            '@type': 'Organization',
            name: 'Hiperalia',
          },
        }}
      />
      {showDesktopLayout && (
        <>
          <Breadcrumb data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:255:10" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="255" data-component-file="ProductPageClient.tsx" data-component-name="Breadcrumb" data-component-content="%7B%22elementName%22%3A%22Breadcrumb%22%7D"
            items={
              breadcrumbs && breadcrumbs.length > 0
                ? breadcrumbs
                : [{ label: 'Productos', href: '/tienda' }]
            }
          />

          <ProductLayout data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:263:10" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="263" data-component-file="ProductPageClient.tsx" data-component-name="ProductLayout" data-component-content="%7B%22elementName%22%3A%22ProductLayout%22%7D">
            <ProductGallery data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:264:12" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="264" data-component-file="ProductPageClient.tsx" data-component-name="ProductGallery" data-component-content="%7B%22elementName%22%3A%22ProductGallery%22%7D"
              productId={product.id}
              mainImages={product.images || []}
              thumbnails={product.images || []}
              rating={0}
              reviewCount={0}
            //onFavoriteToggle={toggleFavorite}
            //isFavorite={isFavorite}
            />

            <ProductDetails data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:274:12" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="274" data-component-file="ProductPageClient.tsx" data-component-name="ProductDetails" data-component-content="%7B%22elementName%22%3A%22ProductDetails%22%2C%22className%22%3A%22max-w-%5B535px%5D%22%7D"
              product={product}
              onAddToCart={handleAddToCart}
              sugerenciasDegustacion={getSugerenciasDegustacion()}
              className="max-w-[535px]"
            />
          </ProductLayout>

          <ProductInfoSection data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:282:10" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="282" data-component-file="ProductPageClient.tsx" data-component-name="ProductInfoSection" data-component-content="%7B%22elementName%22%3A%22ProductInfoSection%22%7D"
            product={product}
            nutritionalInfo={(() => {
              const nutritionalData = getNutritionalInfo();
              const ingredientsData = getIngredients();

              if (nutritionalData.length === 0 && !ingredientsData) {
                return null; // No mostrar nada si no hay datos
              }

              return {
                informacion_nutricional: '',
                ingredientes: ingredientsData,
                Informacion_Nutricional_Lista: nutritionalData,
              };
            })()}
          />

          <RelatedProductsSection data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:300:10" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="300" data-component-file="ProductPageClient.tsx" data-component-name="RelatedProductsSection" data-component-content="%7B%22elementName%22%3A%22RelatedProductsSection%22%7D"
            breadcrumbMenu={breadcrumbMenu}
            onFavoriteToggle={handleProductFavoriteToggle}
            relatedProducts={relatedProducts}
          />

          {isOtherCustomersVisible() && (
            <OtherProductsSection data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:307:12" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="307" data-component-file="ProductPageClient.tsx" data-component-name="OtherProductsSection" data-component-content="%7B%22elementName%22%3A%22OtherProductsSection%22%7D"
              onFavoriteToggle={handleProductFavoriteToggle}
              otherProducts={otherProducts}
              otherCustomersData={{
                titulo_seccion: getOtherCustomersTitle(),
                productos: getRelatedProducts().map((p) => ({
                  id: p.id,
                  id_producto_prestashop: p.Prestashop_ID_Producto,
                  nombre_prestashop: p.Prestashop_Nombre_Producto,
                })),
              }}
            />
          )}

          {/*<ResponsiveSpacing padding="lg">
            <ProductReviews reviews={allReviews} onReviewSubmit={handleReviewSubmit} />
          </ResponsiveSpacing>*/}

          {isFAQsVisible() && (
            <FAQSection data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:326:12" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="326" data-component-file="ProductPageClient.tsx" data-component-name="FAQSection" data-component-content="%7B%22elementName%22%3A%22FAQSection%22%7D"
              productName={product.title}
              faqItems={getFAQs().map((faq) => ({
                id: faq.id,
                Pregunta: faq.Pregunta,
                Respuesta: faq.Respuesta,
              }))}
            />
          )}
        </>
      )}
      {showMobileLayout && (
        <MobileWrapper data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:338:8" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="338" data-component-file="ProductPageClient.tsx" data-component-name="MobileWrapper" data-component-content="%7B%22elementName%22%3A%22MobileWrapper%22%7D">
          <div data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:339:10" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="339" data-component-file="ProductPageClient.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-global-10%20min-h-screen%22%7D" className="bg-global-10 min-h-screen">
            <BreadcrumbMobile data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:340:12" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="340" data-component-file="ProductPageClient.tsx" data-component-name="BreadcrumbMobile" data-component-content="%7B%22elementName%22%3A%22BreadcrumbMobile%22%7D"
              items={
                breadcrumbs && breadcrumbs.length > 0 ? breadcrumbs : [{ label: '', href: '/' }]
              }
            />

            <ProductGalleryMobile data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:346:12" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="346" data-component-file="ProductPageClient.tsx" data-component-name="ProductGalleryMobile" data-component-content="%7B%22elementName%22%3A%22ProductGalleryMobile%22%7D"
              productId={product.id}
              mainImage={
                product.images?.find((image) => image.isMain) ||
                product.images?.[0] || {
                  id: 0,
                  src: '',
                  alt: '',
                  isMain: false,
                }
              }
              rating={0}
              reviewCount={0}
            //onFavoriteToggle={toggleFavorite}
            //isFavorite={isFavorite}
            />

            <ProductDetailsMobile data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:363:12" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="363" data-component-file="ProductPageClient.tsx" data-component-name="ProductDetailsMobile" data-component-content="%7B%22elementName%22%3A%22ProductDetailsMobile%22%7D"
              title={product.title}
              brand={product.brand}
              reference={product.reference}
              description={product.description}
              shortDescription={product.shortDescription}
              price={product.priceFinalInc}
              originalPrice={product.priceOriginalInc}
              discount={product.discountInc}
              sugerenciasDegustacion={getSugerenciasDegustacion()}
              onAddToCart={handleAddToCart}
            />

            <ProductInfoSectionMobile data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:376:12" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="376" data-component-file="ProductPageClient.tsx" data-component-name="ProductInfoSectionMobile" data-component-content="%7B%22elementName%22%3A%22ProductInfoSectionMobile%22%7D"
              product={product}
              nutritionalInfo={(() => {
                const nutritionalData = getNutritionalInfo();
                const ingredientsData = getIngredients();

                if (nutritionalData.length === 0 && !ingredientsData) {
                  return null; // No mostrar nada si no hay datos
                }

                return {
                  informacion_nutricional: '',
                  ingredientes: ingredientsData,
                  Informacion_Nutricional_Lista: nutritionalData,
                };
              })()}
            />

            <RelatedProductsSectionMobile data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:394:12" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="394" data-component-file="ProductPageClient.tsx" data-component-name="RelatedProductsSectionMobile" data-component-content="%7B%22elementName%22%3A%22RelatedProductsSectionMobile%22%7D"
              onFavoriteToggle={handleProductFavoriteToggle}
              relatedProducts={relatedProducts}
            />

            {isOtherCustomersVisible() && (
              <OtherProductsSectionMobile data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:400:14" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="400" data-component-file="ProductPageClient.tsx" data-component-name="OtherProductsSectionMobile" data-component-content="%7B%22elementName%22%3A%22OtherProductsSectionMobile%22%7D"
                onFavoriteToggle={handleProductFavoriteToggle}
                otherProducts={otherProducts}
                otherCustomersData={{
                  titulo_seccion: getOtherCustomersTitle(),
                  productos: getRelatedProducts().map((p) => ({
                    id: p.id,
                    id_producto_prestashop: p.Prestashop_ID_Producto,
                    nombre_prestashop: p.Prestashop_Nombre_Producto,
                  })),
                }}
              />
            )}

            {/*<ProductReviewsMobile reviews={allReviews} onReviewSubmit={handleReviewSubmit} />*/}

            {isFAQsVisible() && (
              <FAQSectionMobile data-component-id="src/app/(tienda)/[slug]/ProductPageClient.tsx:417:14" data-component-path="src/app/(tienda)/[slug]/ProductPageClient.tsx" data-component-line="417" data-component-file="ProductPageClient.tsx" data-component-name="FAQSectionMobile" data-component-content="%7B%22elementName%22%3A%22FAQSectionMobile%22%7D"
                productName={product.title}
                faqItems={getFAQs().map((faq) => ({
                  id: faq.id,
                  Pregunta: faq.Pregunta,
                  Respuesta: faq.Respuesta,
                }))}
              />
            )}
          </div>
        </MobileWrapper>
      )}
    </ContentWrapper>
  );
};

export default ProductPageClient;
